import { validateUsingCallback } from 'modules/interaction/utils/validateUsingCallback'
import {
  ActivityModel,
  ActivityValue,
  FlatActivityModel,
  ValidationStatus
} from 'types/shared'
import { VALIDATION_STATUSES } from './constants'
import {
  AllowedLocales,
  isValidDateInterval,
  isValidDateTime,
  todayAtBeginningOfDay,
  todayAtEndOfDay
} from './date'

const DEFAULT_CHARACTER_LIMIT = 2000

const hasValidationCallback = (
  activityModel: Pick<ActivityModel, 'validation_activity_callback'>
) => !!activityModel.validation_activity_callback

type ErrorMatcherContext = {
  lang: AllowedLocales
}

type ErrorMatcher = {
  helperText: {
    des: string
    complement?: string
  }
  matcher: (
    value: NonNullable<ActivityModel['value']>,
    activityModel: ActivityModel | FlatActivityModel,
    context: ErrorMatcherContext
  ) => boolean
}

const emptinessErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) => {
    const isAutocompleteMultiselection =
      activityModel.content_type === 'autocomplete_multiselection'
    const isEmptyValue =
      value === '' ||
      (activityModel.information_request_callback &&
        typeof value !== 'object' &&
        !isAutocompleteMultiselection)

    return Boolean(activityModel.required && isEmptyValue)
  }
}

const regexErrorMatcher: ErrorMatcher = {
  helperText: { des: 'invalid-format' },
  matcher: (value, activityModel) =>
    Boolean(
      activityModel.content_type !== 'date' &&
        activityModel.content_type !== 'datetime' &&
        'validation_regex' in activityModel &&
        activityModel.validation_regex &&
        value !== '' &&
        !new RegExp(activityModel.validation_regex).test(value.toString())
    )
}

const autocompleteErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) => {
    const isString = activityModel.content_type === 'string'
    const hasFilteraction = !!activityModel.information_request_filteraction

    const isAutocomplete =
      isString && (hasValidationCallback(activityModel) || hasFilteraction)

    return Boolean(
      isAutocomplete &&
        activityModel.required &&
        (typeof value === 'string' || (!value.id && !value.des))
    )
  }
}

const dateErrorMatcher: ErrorMatcher = {
  helperText: {
    des: 'invalid-date-title',
    complement: 'invalid-date-description'
  },
  matcher: (value, activityModel, { lang }) =>
    (activityModel.content_type === 'date' ||
      activityModel.content_type === 'datetime') &&
    !isValidDateTime({
      dateTime: typeof value === 'string' ? value : '',
      contentType: activityModel.content_type,
      lang
    })
}

const documentErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) => {
    let parsedValue: { file?: string } = {
      file: ''
    }
    try {
      parsedValue = JSON.parse(
        typeof value === 'string' ? value : '{ "file": "" }'
      )
    } catch (e) {}

    return Boolean(
      (activityModel.content_type === 'document_photo' ||
        activityModel.content_type === 'document_video') &&
        !parsedValue.file &&
        activityModel.required
    )
  }
}

const otpErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) =>
    activityModel.content_type === 'one_time_password' &&
    (typeof value === 'object' ? value.des : value)?.replaceAll(' ', '')
      ?.length !== activityModel.length
}

const agreementTermErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) => {
    const isAgreementTerm = activityModel.content_type === 'agreement_term'

    if (!isAgreementTerm) return false

    const isAgreementTermChecked =
      typeof value === 'string' && JSON.parse(value)?.checked === '1'

    return isAgreementTerm && !isAgreementTermChecked
  }
}

const drawErrorMatcher: ErrorMatcher = {
  helperText: { des: 'is-required-field' },
  matcher: (value, activityModel) => {
    const isDraw = activityModel.content_type === 'draw'

    if (!isDraw) return false

    const isDrawChecked =
      typeof value === 'string' && !!JSON.parse(value)?.base64

    return isDraw && !isDrawChecked
  }
}

const minDateErrorMatcher: ErrorMatcher = {
  helperText: { des: 'min-date-title', complement: 'min-date-description' },
  matcher: (value, activityModel, { lang }) => {
    if (
      activityModel.content_type !== 'date' &&
      activityModel.content_type !== 'datetime'
    ) {
      return false
    }

    let minFromNow = activityModel.min_from_now && new Date().toISOString()

    if (activityModel.content_type === 'date' && minFromNow)
      minFromNow = todayAtBeginningOfDay()

    const minYear = activityModel.minimum_year
      ? new Date(`${activityModel.minimum_year}-01-01T00:00:00`).toISOString()
      : undefined

    if (minFromNow || minYear) {
      return !isValidDateInterval({
        dateTime: typeof value === 'string' ? value : '',
        minDate: minFromNow || minYear,
        maxDate: undefined,
        lang
      })
    }

    return false
  }
}

const maxDateErrorMatcher: ErrorMatcher = {
  helperText: { des: 'max-date-title', complement: 'max-date-description' },
  matcher: (value, activityModel, { lang }) => {
    if (
      activityModel.content_type !== 'date' &&
      activityModel.content_type !== 'datetime'
    ) {
      return false
    }

    let maxFromNow = activityModel.max_from_now && new Date().toISOString()

    if (activityModel.content_type === 'date' && maxFromNow)
      maxFromNow = todayAtEndOfDay()

    const maxYear = activityModel.maximum_year
      ? new Date(`${activityModel.maximum_year}-12-31T23:59:59`).toISOString()
      : undefined

    if (maxFromNow || maxYear) {
      return !isValidDateInterval({
        dateTime: typeof value === 'string' ? value : '',
        minDate: undefined,
        maxDate: maxFromNow || maxYear,
        lang
      })
    }

    return false
  }
}

const characterLimitErrorMatcher: ErrorMatcher = {
  helperText: { des: 'character-limit-exceeded' },
  matcher: (value, activityModel) => {
    const isTextArea = activityModel.content_type === 'textarea'
    const hasFilteraction = !!activityModel.information_request_filteraction
    const isString =
      activityModel.content_type === 'string' &&
      !hasValidationCallback(activityModel) &&
      !hasFilteraction

    return (
      (isTextArea || isString) &&
      typeof value === 'string' &&
      value.length > (activityModel.character_limit || DEFAULT_CHARACTER_LIMIT)
    )
  }
}

const errorMatchers = [
  emptinessErrorMatcher,
  regexErrorMatcher,
  autocompleteErrorMatcher,
  dateErrorMatcher,
  documentErrorMatcher,
  otpErrorMatcher,
  agreementTermErrorMatcher,
  drawErrorMatcher,
  minDateErrorMatcher,
  maxDateErrorMatcher,
  characterLimitErrorMatcher
]

export type ValidateFlatActivityValueProps = {
  context: ErrorMatcherContext
  activityModel: FlatActivityModel
  value?: ActivityValue
}

export const validateFlatActivityValue = async ({
  context,
  value,
  activityModel
}: ValidateFlatActivityValueProps): Promise<{
  validationStatus: ValidationStatus
  helperText?: {
    des: string
    complement?: string
  }
}> => {
  if (value === undefined) {
    return { validationStatus: VALIDATION_STATUSES.PENDING }
  }

  const errorHelperText = errorMatchers.find(({ matcher }) =>
    matcher(value, activityModel, context)
  )?.helperText

  if (errorHelperText) {
    return {
      validationStatus: VALIDATION_STATUSES.INVALID,
      helperText: errorHelperText
    }
  }

  const shouldCallValidationCallback =
    hasValidationCallback(activityModel) && value !== ''
  const isEmptyOptionalValue = value === '' && !activityModel.required

  if (!shouldCallValidationCallback && !isEmptyOptionalValue) {
    return {
      validationStatus: VALIDATION_STATUSES.VALID,
      helperText: { des: 'activity-input.validation.is_valid' }
    }
  }

  if (shouldCallValidationCallback) {
    return validateUsingCallback({ ...activityModel, value })
  }

  return { validationStatus: 'pending' }
}
